// TODO DDL 2025-01-01 补充各种场景说明
// 获取APP选择城市或者获取定位城市
// 若页面有直接从 query 读取城市参数需求，应当在页面中处理
// https://km.sankuai.com/page/111645869
import Cookies from 'js-cookie'
import Chain from '../base/chain'
import knbUse from '../knb/knb-use'
import Agent from '../agent'
import getURLSearchParam from '../base/url'
import { concurrencyGetLocation, Coords } from './geo'
import { DEFAULT_CITY, geo2phx, isOnSaleCity, getCityByCityId, transCityIdByType } from './api'
import { reportInfo, reportWarn } from '../owl'
import { E_ProcessEnv, getProcessEnv } from '../base/env'
import { CityItem } from './types'
import { errorHandler, isResponseError } from '../axios/response'
import debug from '../base/debug'
import mitt from 'mitt'

const debugCity = debug.city

interface CityParam {
  located?: boolean
  needName?: boolean
}

class City {
  private city: CityItem | undefined
  private locatedCity: CityItem | undefined
  private queue: [CityParam, Function][] = []
  private initStack: CityParam[] = []
  private emitter = mitt()
  private $bus = {
    $on: this.emitter.on,
    $off: this.emitter.off,
    $emit: this.emitter.emit,
  }

  constructor() {
    this.$bus.$on('city', () => {
      this.queue
        .filter(([{ located }]) => {
          return !located
        })
        .forEach(([{ needName }, resolve]) => {
          resolve(needName ? this.city : this.city?.id)
        })
    })
    this.$bus.$on('locatedCity', () => {
      this.queue
        .filter(([{ located }]) => {
          return located
        })
        .forEach(([{ needName }, resolve]) => {
          resolve(needName ? this.locatedCity : this.locatedCity?.id)
        })
    })
  }

  public get({ located = false, needName = false }: CityParam = {}): Promise<number | CityItem | undefined> {
    let ps: Promise<CityItem | number | undefined> | undefined
    if (located) {
      if (!this.locatedCity) {
        ps = new Promise((resolve) => {
          this.queue.push([{ located, needName }, resolve])
        })
      }
    } else {
      if (!this.city) {
        ps = new Promise((resolve) => {
          this.queue.push([{ located, needName }, resolve])
        })
      }
    }
    if (ps) {
      this.init({ located, needName })
      return ps
    }
    if (debugCity) {
      console.log('缓存城市:', {
        city: this.city,
        locatedCity: this.locatedCity,
      })
    }
    if (needName) {
      return Promise.resolve(located ? this.locatedCity : this.city)
    }
    return Promise.resolve(located ? this.locatedCity?.id : this.city?.id)
  }

  private async _getQueryCityId() {
    let queryCityId = parseInt(
      getURLSearchParam('phx_city_id') || getURLSearchParam('cityId') || getURLSearchParam('cityid'),
    )
    if (Agent.groupMpWebview) {
      queryCityId = parseInt(getURLSearchParam('ci'))
    }
    if (!queryCityId || isNaN(queryCityId)) {
      return Chain.NEXT
    }
    if (queryCityId > 100000) {
      return queryCityId
    }
    // 不能明确确定是美团还是点评城市id
    if (Agent.dianping || Agent.dianping_wxapp) {
      const phxcityId = await transCityIdByType(queryCityId, 3).then((res: CityItem) => {
        if (res && res.id) {
          return res.id
        }
        return Chain.NEXT
      })
      return phxcityId
    }
    // 美团
    const phxcityId = await transCityIdByType(queryCityId, 2).then((res: CityItem) => {
      if (res && res.id) {
        return res.id
      }
      return Chain.NEXT
    })
    if (phxcityId) {
      return phxcityId
    }
    return Chain.NEXT
  }

  private _getCookieCityId() {
    let id = Cookies.get('phx_city_id')
    if (id === 'undefined') {
      Cookies.remove('phx_city_id')
      id = undefined
    }
    if (id && Number(id) > 100000) {
      return Number(id)
    }
    return Chain.NEXT
  }

  private _nativeGetCityId() {
    return new Promise((resolve) => {
      const sceneToken = getProcessEnv(E_ProcessEnv.VUE_APP_SCENE_TOKEN_CITY) || undefined
      if (!sceneToken) {
        reportInfo({
          name: 'getCity sceneToken 未传',
        })
      }
      knbUse('getCity', {
        sceneToken,
        success: async (city: { type: number; cityId: number }) => {
          try {
            // 避免部分版本没返回或返回错误
            if (Agent.meituan) city.type = 2
            if (Agent.dianping) city.type = 3
            if (Agent.phx) city.type = 1
            if (!city || !city.cityId) {
              reportWarn({
                name: '城市获取出错',
                global: true,
                msg: city,
              })
            }
            const phxcityId = await transCityIdByType(city.cityId, city.type).then((res: CityItem) => {
              if (res && res.id) {
                return resolve(res.id)
              } else {
                return resolve(Chain.NEXT)
              }
            })
            return resolve(phxcityId)
          } catch (err) {
            console.error('[city]', err)
            return resolve(Chain.NEXT)
          }
        },
        fail: (err: any) => {
          console.error(err)
          reportWarn({
            name: 'KNB getCity 失败' + err.errorCode,
            msg: err,
          })
          return resolve(Chain.NEXT)
        },
      })
    })
  }

  private async _getLocatedCityId() {
    try {
      const locCityId = getURLSearchParam('phx_loc_city_id') || getURLSearchParam('locCityId')
      if (locCityId && Number(locCityId) > 100000) {
        return locCityId
      }
      const { lat: latitude, lng: longitude }: Coords = await new Promise((resolve) => {
        concurrencyGetLocation((res: Coords) => {
          resolve(res)
        })
      })
      if (latitude && longitude) {
        const id = await geo2phx({
          longitude,
          latitude,
        })
        return id
      }
      return Chain.NEXT
    } catch (err) {
      if (isResponseError(err) && err.status !== -1) {
        reportWarn({
          name: 'GET_LOCATED_CITY_ID_ERR',
          msg: err,
          global: true,
        })
      }
      return Chain.NEXT
    }
  }

  private async _fallbackDefaultId() {
    if (Agent.native || Agent.weixin) {
      reportInfo({ name: '城市兜底', global: true })
    }
    return DEFAULT_CITY.id
  }

  private async _getCityId() {
    const c1 = new Chain<number>(this._getQueryCityId)
    // 酒店前置TAB页面, 从 cookie 读
    const c2 = new Chain<number>(this._getCookieCityId)
    const c3 = new Chain<number>(this._nativeGetCityId)
    const c4 = new Chain<number>(this._getLocatedCityId)
    const c5 = new Chain<number>(this._fallbackDefaultId)
    let rlist: Chain<number>[] = [c1, c2, c4, c5]
    if (Agent.native) {
      // native 比非 native 多一个流程
      rlist = [c1, c2, c3, c4, c5]
    }
    for (let i = 0, len = rlist.length - 1; i < len; i++) {
      rlist[i]!.setNext(rlist[i + 1] as Chain<number>)
    }
    const result = await c1.run()
    return result
  }

  // 民宿app直接扫码有phx_city_id
  // app cookie 中 cityid 不同 app 写入的不一致
  // 太阳位 url 会传 cityId
  private async getCityId() {
    try {
      const cityId = await this._getCityId()
      if (cityId !== DEFAULT_CITY.id) {
        const isOnSale = await isOnSaleCity(cityId)
        if (!isOnSale) {
          return DEFAULT_CITY.id
        }
      }
      return cityId
    } catch (error) {
      errorHandler(error, {})
    }
  }

  private async getLocatedCityId() {
    try {
      const c1 = new Chain<number>(this._getLocatedCityId)
      const c2 = new Chain<number>(this._fallbackDefaultId)
      c1.setNext(c2)
      const cityId = await c1.run()
      if (cityId !== DEFAULT_CITY.id) {
        const isOnSale = await isOnSaleCity(cityId)
        if (!isOnSale) {
          return DEFAULT_CITY.id
        }
      }
      return cityId
    } catch (error) {
      errorHandler(error, {})
    }
  }

  private async getCity(cityId?: number | undefined) {
    try {
      if (!cityId) cityId = await this.getCityId()
      const { id, chineseName } = await getCityByCityId(cityId)
      return { id, chineseName }
    } catch (err) {
      return DEFAULT_CITY
    }
  }

  private async getLocatedCity(locatedCityId?: number | undefined) {
    try {
      if (!locatedCityId) locatedCityId = await this.getLocatedCityId()
      const { id, chineseName } = await getCityByCityId(locatedCityId)
      return { id, chineseName }
    } catch (err) {
      return DEFAULT_CITY
    }
  }

  private async init({ located, needName }: CityParam) {
    const inited = this.initStack.find((param) => {
      return param.located === located
    })
    if (inited) return
    this.initStack.push({ located, needName })
    if (debugCity) {
      console.log(this.initStack)
    }
    if (located) {
      if (!this.locatedCity) {
        this.locatedCity = await this.getLocatedCity()
      }
      this.$bus.$emit('locatedCity')
    } else {
      if (!this.city) {
        this.city = await this.getCity()
      }
      this.$bus.$emit('city')
    }
  }
}

export default new City()
